import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Warehouse as WarehouseIcon,
  Input as ReceptionIcon,
  Inventory as InventoryIcon,
  PriorityHigh as UrgentIcon,
  Star as VipIcon,
  LocalShipping as ShippingIcon,
  Timeline as TrackingIcon,
} from '@mui/icons-material';

const WarehouseIndex = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [byDestination, setByDestination] = useState([]);
  const [urgent, setUrgent] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get(endpoints.warehouse.stats);
      setStats(res.data.data.stats);
      setByDestination(res.data.data.by_destination || []);
      setUrgent([...(res.data.data.urgent || []), ...(res.data.data.vip || [])]);
    } catch (error) {
      console.error('Error:', error);
      setError('Error al cargar estadísticas del warehouse');
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    {
      title: 'Recepción',
      description: 'Registrar llegada de carga',
      link: '/warehouse/reception',
      icon: ReceptionIcon,
      gradient: 'linear-gradient(135deg, #2E7D32 0%, #388E3C 100%)',
    },
    {
      title: 'Inventario',
      description: 'Ver carga en depósito',
      link: '/warehouse/inventory',
      icon: InventoryIcon,
      gradient: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
    },
    {
      title: 'Tracking AWB',
      description: 'Consultar estado de guías',
      link: '/warehouse/tracking',
      icon: TrackingIcon,
      gradient: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
    },
  ];

  const StatCard = ({ value, label, color, icon: Icon }) => (
    <Card sx={{ borderLeft: 4, borderColor: `${color}.main` }}>
      <CardContent sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        {Icon && <Icon sx={{ fontSize: 32, color: `${color}.main` }} />}
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {value ?? '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, #37474F 0%, #455A64 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarehouseIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Warehouse Operations
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Gestión de recepción, inventario y asignación de carga
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Estadísticas Generales */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={2}>
            <StatCard
              value={stats.total}
              label="Total Items"
              color="primary"
              icon={InventoryIcon}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              value={stats.received}
              label="Recibidos"
              color="success"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              value={stats.staged}
              label="Asignados"
              color="info"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              value={stats.urgent}
              label="Urgentes"
              color="warning"
              icon={UrgentIcon}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              value={`${((stats.total_weight || 0) / 1000).toFixed(1)}t`}
              label="Peso Total"
              color="secondary"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              value={stats.total_pieces}
              label="Piezas"
              color="primary"
            />
          </Grid>
        </Grid>
      )}

      {/* Módulos */}
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
        Operaciones
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {modules.map((module) => {
          const IconComponent = module.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={module.title}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={module.link}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <Box
                    sx={{
                      background: module.gradient,
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconComponent sx={{ fontSize: 56, color: 'white' }} />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {module.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {module.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Inventario por Destino */}
      {byDestination.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Inventario por Destino
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Destino</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell align="right">Piezas</TableCell>
                    <TableCell align="right">Peso (kg)</TableCell>
                    <TableCell sx={{ width: 200 }}>Distribución</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byDestination.slice(0, 8).map((dest) => {
                    const totalWeight = byDestination.reduce((sum, d) => sum + (parseFloat(d.weight) || 0), 0);
                    const percentage = totalWeight > 0 ? ((parseFloat(dest.weight) || 0) / totalWeight) * 100 : 0;
                    
                    return (
                      <TableRow key={dest.destination} hover>
                        <TableCell>
                          <Chip label={dest.destination} color="primary" size="small" />
                        </TableCell>
                        <TableCell align="right">{dest.items}</TableCell>
                        <TableCell align="right">{dest.pieces}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {parseFloat(dest.weight || 0).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 40 }}>
                              {percentage.toFixed(0)}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Items Urgentes/VIP */}
      {urgent.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <UrgentIcon color="warning" />
              <Typography variant="h6" fontWeight="bold">
                Items Prioritarios
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Referencia</TableCell>
                    <TableCell>AWB</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell align="right">Piezas</TableCell>
                    <TableCell align="right">Peso</TableCell>
                    <TableCell>Prioridad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {urgent.slice(0, 5).map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography fontWeight="bold">{item.reference}</Typography>
                      </TableCell>
                      <TableCell>
                        {item.awb ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">{item.awb}</Typography>
                            <Tooltip title="Ver Tracking">
                              <IconButton 
                                size="small" 
                                component={Link}
                                to={`/warehouse/tracking?awb=${item.awb}`}
                                color="primary"
                              >
                                <TrackingIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip label={item.destination} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{item.pieces_remaining}</TableCell>
                      <TableCell align="right">{parseFloat(item.weight_remaining || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.priority?.toUpperCase()}
                          size="small"
                          color={item.priority === 'vip' ? 'secondary' : 'warning'}
                          icon={item.priority === 'vip' ? <VipIcon /> : <UrgentIcon />}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default WarehouseIndex;




